import { initMainHeader } from '@/js/src/header-init';
import {
    getGPCPreference,
    setGPCPreference,
    setGpcOptedOut,
    getConsentValue,
    parseCookiePrefs,
    applyConsentSelection,
    initConsentSync,
    itcEventBus,
    BUS_EVENTS,
} from 'common/ts';
import '../scss/pages/privacy-choices.scss';

const LABEL_ANALYTICS_ON  = 'Analytics tracking enabled';
const LABEL_ANALYTICS_OFF = 'Analytics tracking disabled';
const LABEL_MARKETING_ON  = 'marketing tracking enabled';
const LABEL_MARKETING_OFF = 'marketing tracking disabled';
const LABEL_GPC_OPTED_OUT = 'Opted out';
const LABEL_GPC_OPTED_IN  = 'Opted in';

initMainHeader();

document.addEventListener('DOMContentLoaded', () => {
    const gpcToggle     = document.getElementById('pc-gpc-toggle');
    const gpcStateLabel = document.getElementById('pc-gpc-state-label');
    const gpcStatus     = document.getElementById('pc-gpc-status');

    const analyticsToggle = document.getElementById('pc-analytics-toggle');
    const marketingToggle = document.getElementById('pc-marketing-toggle');
    const cookieStatus    = document.getElementById('pc-cookie-status');

    // ── GPC browser signal badge ──────────────────────────────────────────
    const gpcBrowserSignal = navigator.globalPrivacyControl === true;
    const gpcBadge = document.getElementById('pc-gpc-browser-status');
    if (gpcBadge) {
        gpcBadge.textContent = gpcBrowserSignal ? 'Enabled' : 'Not Detected';
        gpcBadge.className   = `pc-badge pc-badge--${gpcBrowserSignal ? 'on' : 'off'}`;
    }

    // ── Cookie toggles: set state based on saved consent ─────────────────
    const consentValue = getConsentValue();
    let analytics, marketing;
    if (!consentValue || consentValue === 'all_denied') {
        analytics = false; marketing = false;
    } else if (consentValue === 'all_granted') {
        analytics = true; marketing = true;
    } else {
        ({ analytics, marketing } = parseCookiePrefs(consentValue));
    }
    analyticsToggle.checked = analytics;
    marketingToggle.checked = marketing;

    // ── Status message helper ─────────────────────────────────────────────
    function showStatus(el, text, type) {
        el.className = `pc-status pc-status--${type}`;
        el.innerHTML = `<span class="pc-status__icon">${type === 'success' ? '✓' : '⚠'}</span><span>${text}</span>`;
    }

    function hideStatus(el) {
        el.className = 'pc-status';
    }

    function showCookieStatus(analyticsOn, marketingOn) {
        const analyticsLabel = analyticsOn ? LABEL_ANALYTICS_ON : LABEL_ANALYTICS_OFF;
        const marketingLabel = marketingOn ? LABEL_MARKETING_ON : LABEL_MARKETING_OFF;
        const allOn = analyticsOn && marketingOn;
        showStatus(cookieStatus, `${analyticsLabel}, ${marketingLabel}.`, allOn ? 'success' : 'warning');
    }

    showCookieStatus(analytics, marketing);

    // ── Save cookie prefs and update status message ───────────────────────
    function saveCookiePrefs() {
        const analyticsOn = analyticsToggle.checked;
        const marketingOn = marketingToggle.checked;
        applyConsentSelection(analyticsOn, marketingOn);
        showCookieStatus(analyticsOn, marketingOn);
    }

    // ── Lock/unlock cookie card when GPC opt-out changes ─────────────────
    function applyGpcOptOut(optedOut, skipSave = false) {
        if (optedOut) {
            analyticsToggle.checked = false;
            marketingToggle.checked = false;
            if (!skipSave) saveCookiePrefs();
        }
        analyticsToggle.disabled = optedOut;
        marketingToggle.disabled = optedOut;
        const cookieCard = analyticsToggle.closest('.privacy-card');
        if (cookieCard) cookieCard.classList.toggle('pc-card--disabled', optedOut);
        setGpcOptedOut(optedOut);
        showStatus(
            gpcStatus,
            optedOut ? 'You have opted out of data sale/sharing.' : 'You have allowed data sharing.',
            optedOut ? 'warning' : 'success'
        );
    }

    // ── GPC toggle initial state ──────────────────────────────────────────
    const gpcPref    = getGPCPreference();
    const isOptedOut = gpcPref === 'opted_out' || gpcPref === 'browser';
    gpcToggle.checked         = isOptedOut;
    gpcStateLabel.textContent = isOptedOut ? LABEL_GPC_OPTED_OUT : LABEL_GPC_OPTED_IN;
    applyGpcOptOut(isOptedOut, true);

    gpcToggle.addEventListener('change', () => {
        const optedOut = gpcToggle.checked;
        const previousOptedOut = !optedOut;

        gpcToggle.disabled = true;
        gpcStateLabel.textContent = optedOut ? LABEL_GPC_OPTED_OUT : LABEL_GPC_OPTED_IN;
        applyGpcOptOut(optedOut);

        setGPCPreference(optedOut ? 'opted_out' : 'opted_in')
            .then(() => {
                gpcToggle.disabled = false;
            })
            .catch(() => {
                gpcToggle.checked = previousOptedOut;
                gpcStateLabel.textContent = previousOptedOut ? LABEL_GPC_OPTED_OUT : LABEL_GPC_OPTED_IN;
                applyGpcOptOut(previousOptedOut);
                showStatus(gpcStatus, 'Could not save preference. Please try again.', 'warning');
                gpcToggle.disabled = false;
            });
    });

    if (gpcBrowserSignal) {
        gpcToggle.disabled = true;
        const gpcCard = gpcToggle.closest('.privacy-card');
        if (gpcCard) gpcCard.classList.add('pc-card--disabled');
        showStatus(gpcStatus, 'Locked by browser GPC signal.', 'warning');
    }

    analyticsToggle.addEventListener('change', saveCookiePrefs);
    marketingToggle.addEventListener('change', saveCookiePrefs);

    // ── Enable consent sync (privacy page only) ───────────────────────────
    initConsentSync();
    itcEventBus.subscribe(({ analytics, marketing }) => {
        analyticsToggle.checked = analytics;
        marketingToggle.checked = marketing;
        const analyticsLabel = analytics ? LABEL_ANALYTICS_ON : LABEL_ANALYTICS_OFF;
        const marketingLabel = marketing ? LABEL_MARKETING_ON : LABEL_MARKETING_OFF;
        const allOn = analytics && marketing;
        showStatus(cookieStatus, `${analyticsLabel}, ${marketingLabel}.`, allOn ? 'success' : 'warning');
    }, BUS_EVENTS.consentChange);
});
